import CustomDropdown from '@/components/CommonComponents/CustomDropdown';
import { formatDateForInput } from '@/helper/utils';
import { StepValidatorRef, useStepValidation } from '@/hooks/useStepValidation';
import { SurveyListItem } from '@/types/survey.types';
import { Plus, Trash2 } from 'lucide-react';
import { forwardRef, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { surveysetCreationType } from './CreateSurveySet';

interface AddSurveysProps {
  surveys: SurveyListItem[];
  isEditing?: boolean;
}

const AddSurveys = forwardRef<StepValidatorRef, AddSurveysProps>(({ surveys, isEditing }, ref) => {
  useStepValidation(ref, ['surveyIds']);
  const {
    getValues,
    register,
    watch,
    setValue,
    formState: { errors },
    control,
  } = useFormContext<surveysetCreationType>();
  const isInitializing = useRef(true);

  const numberOfSurveys = watch('numberOfSurveys');
  const surveyIds = watch('surveyIds');
  const surveySlots = Array.from({ length: numberOfSurveys }, (_, i) => ({
    id: i + 1,
    surveyId: surveyIds?.[i] ?? 0,
  }));

  const handleAddSurvey = () => {
    setValue('numberOfSurveys', numberOfSurveys + 1);
    setValue('surveyIds', [...surveyIds, 0]);
    if (getValues().endDate) {
      const date = new Date(getValues().endDate);
      date.setDate(date.getDate() + 1);
      date.setHours(23, 59, 59, 999);
      const formatDate = formatDateForInput(date.toISOString());
      setValue('endDate', formatDate);
    }
  };

  const handleDeleteSurvey = (index: number) => {
    const updatedSurveyIds = surveyIds.filter((_, i) => i !== index);

    setValue('surveyIds', updatedSurveyIds);
    setValue('numberOfSurveys', updatedSurveyIds.length);

    if (getValues().endDate) {
      const date = new Date(getValues().endDate);
      date.setDate(date.getDate() - 1);
      date.setHours(23, 59, 59, 999);
      const formatDate = formatDateForInput(date.toISOString());
      setValue('endDate', formatDate);
    }
  };

  const getAvailableSurveys = (currentSlotId: number) => {
    const currentSurveyId = surveySlots.find((s) => s.id === currentSlotId)?.surveyId;
    const selectedIds = surveySlots.filter((s) => s.id !== currentSlotId).map((s) => s.surveyId);

    return surveys.filter(
      (survey) =>
        // survey.status && // Only show active surveys
        !selectedIds.includes(survey.id) || survey.id === currentSurveyId,
    );
  };

  return (
    <div className="mx-auto text-[#621C43]">
      <div className="border-b border-b-[#621C43] px-5 py-6 flex justify-between items-center">
        <div className="">
          <h2 className="text-xl font-semibold text-[#883c4a]">
            {isEditing ? 'Edit Survey' : 'Add Survey'}
          </h2>
          <p className="text-sm text-[#883c4a]">
            {isEditing ? 'Add survey to survey-set' : 'Add a ODK form per survey-set day'}
          </p>
        </div>
        <div className="px-6">
          <button
            onClick={handleAddSurvey}
            className="flex items-center gap-2  bg-[#883c4a]  text-white text-sm font-medium px-4 py-2 rounded-md"
          >
            <Plus size={16} /> Add Survey
          </button>
        </div>
      </div>
      <div className="px-6 py-3">
        {surveySlots.length === 0 ? (
          <div className="text-center py-8 text-[#48082D]">
            <p>No surveys added yet. Click "Add Survey" to begin.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {surveySlots.map((slot, index) => {
              const availableSurveys = getAvailableSurveys(slot.id);
              return (
                <div key={slot.id} className="relative">
                  <label className="block text-sm font-medium text-[#48082D] mb-1">
                    Day {index + 1} Survey
                  </label>
                  {index === 0 && (
                    <span className="absolute left-[calc(0.5rem+8ch)] -top-1 text-red-500 pointer-events-none">
                      *
                    </span>
                  )}
                  <div className="flex items-center gap-2 relative">
                    <Controller
                      name={`surveyIds.${index}` as const}
                      control={control}
                      rules={{
                        required: 'Please select a survey',
                        validate: (value) => value > 0 || 'Please select a valid survey',
                      }}
                      render={({ field }) => (
                        <CustomDropdown
                          value={field.value || 0}
                          onChange={field.onChange}
                          options={[
                            { value: 0, label: 'Select Survey' },
                            ...availableSurveys.map((survey) => ({
                              value: survey.id,
                              label: survey.title,
                            })),
                          ]}
                          placeholder="Select Survey"
                          className="bg-[#4A082D33]/10 rounded-lg text-base placeholder:text-base"
                        />
                      )}
                    />

                    {surveySlots.length > 1 && (
                      <button
                        onClick={() => handleDeleteSurvey(index)}
                        className="p-2 text-[#48082D] hover:text-red-600 transition"
                        title="Remove survey"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  {errors.surveyIds?.[index] && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.surveyIds?.[index]?.message}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

export default AddSurveys;
